import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import api from "../api/client";
import { ShieldCheck, CheckCircle2, XCircle, FileText } from "lucide-react";

const KIND_LABEL = {
    creator_assignment: "Creator Assignment Deed",
    buyer_purchase: "Buyer Purchase Agreement",
    transfer_complete: "Title Transfer (Vault release)",
    kyc_verified: "KYC Verified",
    film_registered: "Film Registered",
};

export default function Verify() {
    const { hash } = useParams();
    const [state, setState] = useState({ loading: true, data: null, error: null });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/verify/${hash}`);
                setState({ loading: false, data, error: null });
            } catch (err) {
                setState({ loading: false, data: null, error: err?.response?.data?.detail || "Verification failed" });
            }
        })();
    }, [hash]);

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <section className="max-w-[1000px] mx-auto px-4 md:px-10 pt-14 pb-28">
                <div className="flex items-center gap-3 mb-3 text-[11px] tracking-[0.3em] text-silver-400 uppercase">
                    <ShieldCheck className="w-4 h-4" /> Public Ledger · Verify
                </div>
                <h1 className="font-display text-5xl md:text-6xl leading-none mb-10">
                    Chain-of-title <span className="silver-text italic">verification.</span>
                </h1>

                <div className="mb-8 text-xs tracking-[0.3em] uppercase text-silver-500 break-all">
                    Hash · <span className="text-silver-300 normal-case tracking-normal font-mono text-[11px]">{hash}</span>
                </div>

                {state.loading && <div className="text-silver-500">Verifying…</div>}

                {state.error && (
                    <div className="border border-red-400/30 bg-red-400/5 rounded-sm p-6 text-red-300 flex items-center gap-3" data-testid="verify-failed">
                        <XCircle className="w-5 h-5" /> {state.error}
                    </div>
                )}

                {state.data && (
                    <div className="space-y-6" data-testid="verify-ok">
                        <div className="border border-emerald-300/30 bg-emerald-300/5 rounded-sm p-6 flex items-center gap-4">
                            <CheckCircle2 className="w-6 h-6 text-emerald-300" />
                            <div>
                                <div className="text-emerald-200 text-sm tracking-widest uppercase">Entry found in ledger</div>
                                <div className="text-silver-300 text-sm mt-1">
                                    {KIND_LABEL[state.data.ledger.kind] || state.data.ledger.kind} ·
                                    {" "}{new Date(state.data.ledger.created_at).toLocaleString()}
                                </div>
                            </div>
                        </div>

                        {state.data.film && (
                            <div className="bg-ink-200/60 border border-white/10 rounded-sm p-6">
                                <div className="text-xs tracking-[0.3em] uppercase text-silver-400 mb-4">Film</div>
                                <div className="font-display text-3xl mb-2">{state.data.film.title}</div>
                                <div className="text-silver-400 text-sm">
                                    {state.data.film.country} · {state.data.film.language} · by {state.data.film.creator_name}
                                </div>
                            </div>
                        )}

                        {state.data.deal && (
                            <div className="bg-ink-200/60 border border-white/10 rounded-sm p-6">
                                <div className="text-xs tracking-[0.3em] uppercase text-silver-400 mb-4">Deal</div>
                                <div className="text-silver-300 text-sm font-mono">ID: {state.data.deal.id}</div>
                                <div className="text-silver-300 text-sm mt-1">Status: {state.data.deal.status}</div>
                                <Link to={`/deal/${state.data.deal.id}`} className="mt-4 inline-flex items-center gap-2 text-platinum text-xs tracking-widest uppercase underline">
                                    <FileText className="w-3.5 h-3.5" /> Open deal room
                                </Link>
                            </div>
                        )}

                        <div className="bg-ink-200/60 border border-white/10 rounded-sm p-6">
                            <div className="text-xs tracking-[0.3em] uppercase text-silver-400 mb-3">Ledger preview</div>
                            <pre className="text-silver-300 text-xs whitespace-pre-wrap font-mono">{state.data.ledger.preview}</pre>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
